import { useState, type FormEvent } from 'react'
import { motion } from 'framer-motion'
import { Bot, Eye, EyeOff, LogIn, Mail, UserPlus } from 'lucide-react'
import axios from 'axios'
import { useAuth } from '../AuthContext'
import { requestPasswordReset } from '../api'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Card, CardContent, CardHeader } from './ui/card'

type Mode = 'login' | 'register' | 'forgot'

export function Login() {
  const { login, register } = useAuth()
  const [mode, setMode] = useState<Mode>('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [nombre, setNombre] = useState('')
  const [empresaNombre, setEmpresaNombre] = useState('')
  const [sociedadNombre, setSociedadNombre] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [forgotSent, setForgotSent] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault()
    setError(null)
    setIsSubmitting(true)
    try {
      if (mode === 'login') {
        await login(email, password)
      } else if (mode === 'register') {
        await register({ email, password, nombre, empresa_nombre: empresaNombre, sociedad_nombre: sociedadNombre })
      } else {
        await requestPasswordReset(email)
        setForgotSent(true)
      }
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 409) {
        setError('Ya existe una cuenta con ese email.')
      } else if (axios.isAxiosError(err) && err.response?.status === 401) {
        setError('Credenciales inválidas.')
      } else {
        setError('Algo salió mal. Intenta de nuevo.')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  function switchMode(next: Mode): void {
    setError(null)
    setForgotSent(false)
    setMode(next)
  }

  const titles: Record<Mode, string> = {
    login: 'Iniciar sesión',
    register: 'Crear cuenta',
    forgot: 'Recuperar contraseña',
  }
  const subtitles: Record<Mode, string> = {
    login: 'Accedé al panel de conversaciones.',
    register: 'Registrá tu Empresa y tu primera Sociedad.',
    forgot: 'Te enviamos un enlace para elegir una nueva contraseña.',
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-slate-950 px-4">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(99,102,241,0.25),transparent_45%),radial-gradient(circle_at_80%_75%,rgba(99,102,241,0.18),transparent_40%)]" />
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.04)_1px,transparent_1px)] bg-[size:44px_44px] [mask-image:radial-gradient(ellipse_at_center,black_45%,transparent_75%)]" />

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-sm"
      >
        <Card className="border-white/10 bg-white shadow-2xl shadow-black/40 dark:bg-slate-900">
          <CardHeader className="gap-3">
            <div className="flex size-9 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-indigo-500 text-primary-foreground shadow-sm shadow-primary/30">
              <Bot className="size-4.5" />
            </div>
            <div>
              <h1 className="text-lg font-semibold tracking-tight">{titles[mode]}</h1>
              <p className="text-sm text-muted-foreground">{subtitles[mode]}</p>
            </div>
          </CardHeader>
          <CardContent>
            {mode === 'forgot' && forgotSent ? (
              <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400">
                Si el email existe, te enviamos un enlace de recuperación.
              </p>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col gap-3.5">
                {mode === 'register' && (
                  <>
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="login-nombre">Tu nombre</Label>
                      <Input id="login-nombre" required value={nombre} onChange={(e) => setNombre(e.target.value)} />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="login-empresa">Nombre de la Empresa</Label>
                      <Input
                        id="login-empresa"
                        required
                        value={empresaNombre}
                        onChange={(e) => setEmpresaNombre(e.target.value)}
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="login-sociedad">Nombre de la Sociedad (negocio/marca)</Label>
                      <Input
                        id="login-sociedad"
                        required
                        value={sociedadNombre}
                        onChange={(e) => setSociedadNombre(e.target.value)}
                      />
                    </div>
                  </>
                )}
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="login-email">Email</Label>
                  <Input id="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>
                {mode !== 'forgot' && (
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="login-password">Contraseña</Label>
                    <div className="relative">
                      <Input
                        id="login-password"
                        type={showPassword ? 'text' : 'password'}
                        required
                        minLength={8}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pr-9"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                        className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                )}

                {mode === 'login' && (
                  <button
                    type="button"
                    onClick={() => switchMode('forgot')}
                    className="-mt-1 self-end text-xs text-muted-foreground hover:text-primary"
                  >
                    ¿Olvidaste tu contraseña?
                  </button>
                )}

                {error && <p className="rounded-lg bg-destructive/10 px-3 py-2 text-xs font-medium text-destructive">{error}</p>}

                <Button type="submit" disabled={isSubmitting} className="mt-1">
                  {mode === 'login' && <LogIn className="h-4 w-4" />}
                  {mode === 'register' && <UserPlus className="h-4 w-4" />}
                  {mode === 'forgot' && <Mail className="h-4 w-4" />}
                  {mode === 'login' ? 'Entrar' : mode === 'register' ? 'Crear cuenta' : 'Enviar enlace de recuperación'}
                </Button>
              </form>
            )}

            <button
              type="button"
              onClick={() => switchMode(mode === 'login' ? 'register' : 'login')}
              className="mt-4 w-full text-center text-xs text-muted-foreground hover:text-primary"
            >
              {mode === 'login' ? '¿No tenés cuenta? Registrate' : '¿Ya tenés cuenta? Iniciá sesión'}
            </button>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  )
}
